import { useTranslation } from 'react-i18next'
import { ToggleGroup, ToggleGroupItem } from '@/components/ui/toggle-group'
import { useUiStore, type ContentMode } from '@/stores/uiStore'

/**
 * 创作繁简档位的全局切换。
 *
 * 状态存在 uiStore(并持久化到 localStorage), 所以任一处切换,
 * 所有文字工作页面同步生效 —— 它不是某个面板的局部开关。
 *
 * 左侧带一个「当前: 简易/高级」的状态文字, 让当前档位一眼可读,
 * 不依赖用户去分辨哪个按钮是选中态。
 */
export function ContentModeSwitch({ className }: { className?: string }) {
  const { t } = useTranslation()
  const contentMode = useUiStore((s) => s.contentMode)
  const setContentMode = useUiStore((s) => s.setContentMode)

  return (
    <div className={['flex items-center gap-2', className ?? ''].join(' ').trim()}>
      <span className="flex items-center gap-1.5 whitespace-nowrap text-xs text-muted-foreground">
        {t('contentMode.currentLabel')}
        {/* 当前档位用带背景+边框+投影的徽章凸显, 两档配色不同, 一眼可辨 */}
        <span
          className={[
            'inline-flex items-center rounded-md border px-2 py-0.5 text-xs font-semibold shadow-sm',
            contentMode === 'easy'
              ? 'border-primary/40 bg-primary/15 text-primary'
              : 'border-[color-mix(in_oklch,var(--nf-st-review)_45%,transparent)] bg-[color-mix(in_oklch,var(--nf-st-review)_18%,transparent)] text-[color-mix(in_oklch,var(--nf-st-review)_75%,var(--foreground))]',
          ].join(' ')}
        >
          {contentMode === 'easy' ? t('contentMode.easy') : t('contentMode.advanced')}
        </span>
      </span>
      <ToggleGroup
        className="grid w-[10rem] grid-cols-2"
        type="single"
        value={contentMode}
        onValueChange={(value) => {
          if (value) setContentMode(value as ContentMode)
        }}
        aria-label={t('contentMode.label')}
      >
        <ToggleGroupItem value="easy">{t('contentMode.easy')}</ToggleGroupItem>
        <ToggleGroupItem value="advanced">{t('contentMode.advanced')}</ToggleGroupItem>
      </ToggleGroup>
    </div>
  )
}
